/**
 * NetWorthChart.js — Monthly net worth trend line SVG chart
 *
 * Renders a line chart showing net worth over time with dot markers.
 * Shaded green area below the line. Tap/click a point to show detail.
 * Returns null if fewer than 2 data points.
 *
 * Props:
 *   snapshots — Array of net_worth_snapshots (chronological, oldest first)
 */
import React, { useState, useRef } from 'react';
import { formatCurrency, formatCompact, formatMonthLabel } from '../utils/format';
import theme from '../utils/theme';

const VIEW_W = 300;
const VIEW_H = 160;
const CHART_L = 44;
const CHART_R = 290;
const CHART_T = 10;
const CHART_B = 135;
const CHART_W = CHART_R - CHART_L;
const CHART_H = CHART_B - CHART_T;

export default function NetWorthChart({ snapshots }) {
  const [activeIdx, setActiveIdx] = useState(null);
  const svgRef = useRef(null);

  if (!snapshots || snapshots.length < 2) return null;

  const values = snapshots.map(s => s.net_worth);
  const maxVal = Math.max(...values);
  const minVal = Math.min(...values);
  const padding = Math.max((maxVal - minVal) * 0.15, 500);
  const yMax = maxVal + padding;
  const yMin = minVal - padding;
  const yRange = yMax - yMin || 1;
  const n = snapshots.length;

  const toX = (i) => CHART_L + (i / (n - 1)) * CHART_W;
  const toY = (val) => CHART_T + (1 - (val - yMin) / yRange) * CHART_H;

  // Line points
  const linePoints = snapshots.map((s, i) => `${toX(i)},${toY(s.net_worth)}`).join(' ');

  // Area fill polygon
  const areaPoints = snapshots.map((s, i) => `${toX(i)},${toY(s.net_worth)}`).join(' ')
    + ` ${toX(n - 1)},${CHART_B} ${toX(0)},${CHART_B}`;

  // Y-axis ticks
  const yTicks = [yMax, (yMax + yMin) / 2, yMin].map(v => ({
    y: toY(v),
    label: formatCompact(Math.round(v)),
  }));

  // X-axis labels — show first, middle, last
  const xLabels = [];
  const labelIndices = n <= 4
    ? snapshots.map((_, i) => i)
    : [0, Math.floor(n / 2), n - 1];
  for (const i of labelIndices) {
    const mk = snapshots[i].month_key;
    const [, m] = mk.split('-');
    const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    xLabels.push({ i, label: monthNames[parseInt(m)] });
  }

  const handleInteraction = (e) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const svgX = ((clientX - rect.left) / rect.width) * VIEW_W;
    const idx = Math.round(((svgX - CHART_L) / CHART_W) * (n - 1));
    const clamped = Math.max(0, Math.min(idx, n - 1));
    setActiveIdx(prev => prev === clamped ? null : clamped);
  };

  const activeSnap = activeIdx != null ? snapshots[activeIdx] : null;

  return (
    <div className="networth-chart">
      <h3>Net Worth Trend</h3>
      <div className="networth-chart-wrap">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          onClick={handleInteraction}
          onTouchStart={handleInteraction}
        >
          <defs>
            <linearGradient id="nw-gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={theme.success} stopOpacity="0.25" />
              <stop offset="100%" stopColor={theme.success} stopOpacity="0.02" />
            </linearGradient>
          </defs>

          {/* Area fill */}
          <polygon points={areaPoints} fill="url(#nw-gradient)" />

          {/* Line */}
          <polyline
            points={linePoints}
            fill="none"
            stroke={theme.success}
            strokeWidth="1.8"
            strokeLinejoin="round"
          />

          {/* Dot markers */}
          {snapshots.map((s, i) => (
            <circle
              key={i}
              cx={toX(i)} cy={toY(s.net_worth)}
              r="2.5"
              fill={theme.success}
              stroke="white"
              strokeWidth="0.8"
            />
          ))}

          {/* Active point */}
          {activeIdx != null && (
            <>
              <line
                x1={toX(activeIdx)} y1={CHART_T}
                x2={toX(activeIdx)} y2={CHART_B}
                stroke={theme.textMuted} strokeWidth="0.5" strokeDasharray="2,2"
              />
              <circle
                cx={toX(activeIdx)} cy={toY(snapshots[activeIdx].net_worth)}
                r="4"
                fill={theme.primary} stroke="#fff" strokeWidth="1"
              />
            </>
          )}

          {/* Y-axis labels */}
          {yTicks.map((t, i) => (
            <text key={i} x={CHART_L - 4} y={t.y + 3} className="networth-y-label" textAnchor="end">
              {t.label}
            </text>
          ))}

          {/* X-axis labels */}
          {xLabels.map((l, i) => (
            <text key={i} x={toX(l.i)} y={CHART_B + 12} className="networth-x-label" textAnchor="middle">
              {l.label}
            </text>
          ))}
        </svg>
      </div>

      {/* Detail tooltip */}
      {activeSnap && (
        <div style={{ borderTop: `1px solid ${theme.bgSkeleton}`, paddingTop: '0.5rem', marginTop: '0.25rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: theme.text }}>
              {formatMonthLabel(activeSnap.month_key)}
            </span>
            <span style={{ fontSize: '0.95rem', fontWeight: 700, color: activeSnap.net_worth >= 0 ? theme.success : theme.danger }}>
              {formatCurrency(activeSnap.net_worth, false)}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: theme.textSecondary, marginTop: '0.15rem' }}>
            <span>Assets: {formatCurrency(activeSnap.total_assets, false)}</span>
            <span>Debts: {formatCurrency(activeSnap.total_liabilities, false)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
